<script setup>
import Vue, {ref, inject, computed, watch, provide, reactive, nextTick, onMounted} from "vue";
import trackersService from "@/services/trackers.service";
import useForm from "@/composables/useForm";
import {trackerTypes} from "@/constants/trackers";
import SubmitterFields from "@/components/trackers/trackerEdit/SubmitterFields.vue";
import trackersFoldersService from "@/services/trackers-folders.service";
import Pipeline from "@/components/trackers/trackerEdit/Pipeline.vue";
import SelectTemplate from "@/components/trackers/trackerEdit/SelectTemplate.vue";
import POC from "@/components/trackers/trackerEdit/POC.vue";
import Permissions from "@/components/trackers/trackerEdit/Permissions.vue";
import CustomFields from "@/components/trackers/trackerEdit/CustomFields.vue";
import TimeLine from "@/components/trackers/trackerEdit/Timeline.vue";
import Sharing from "@/components/trackers/trackerEdit/Sharing.vue";
import Categories from "@/components/trackers/trackerEdit/Categories.vue";
import Details from "@/components/trackers/trackerEdit/Details.vue";
import Footer from "@/components/trackers/trackerEdit/Footer.vue";
import Settings from "@/components/trackers/trackerEdit/Settings.vue";
import {decodeIntervalFromServer} from "@/helpers/date";

const emit = defineEmits(['trackerSaved']);

const trackerId = inject('trackerId');
const isNew = inject('isNew');
const trackerType = inject('trackerType');
const folderId = inject('folderId');

const tracker = reactive({
    name: '',
    folder: null,
    categories: [],
    description: '',
    pipeline: null,
    timelinePreset: null,
    startDate: null,
    endDate: null,
    postDate: null,
    type: '',
    enablePipeline: true,
    defaultView: 'pipeline',
    reviewsEnabled: 1,
    needCompleteProfile: false,
    notifyStartups: true,
    hideDetailsUntilSignIn: false,
    allowStartupDeleteSubmission: false,
    onlyReferral: false,
    internalAccessById: false,
    questions: [],
    reviewerFields: [],
    reviewerAggregateFields: [],
    submitterFields: [],
    diligenceFields: [],
    subPeriods: [],
    permissions: [],
    userPermissions: [],
    termsAndConditions: "",
    accessType: "private",
    ecosystems: [],
    emailStages: [],
    enableNotifications: true,
    isCollab: false,
    templateId: null,
    linkedTrackerId: null,
});

const {populateForm} = useForm(tracker);

const selectTemplateStep = ref(false);
const submitterFieldsBlock = ref(null);
const selectTemplateBlock = ref(null);
const pipelineBlock = ref(null);
const timeline = ref([null, null]);
const loading = ref(false);
const initDiligenceFields = ref([]);
const initReferral = ref(false);
const newFilesFieldsCount = ref(0);

const canEditInfo = computed(() => !trackerId.value || tracker.userPermissions.includes('tracker-edit'));

watch(() => tracker.enablePipeline, (value) => {
    if (!value) {
        tracker.defaultView = 'list';
    }
});

watch(() => tracker.onlyReferral, (value) => {
    if (initReferral.value === null) {
        initReferral.value = value;
    } else if (initReferral.value && !value && !confirm("Disabling this option will remove the referral url.\n" +
        "Do you want to proceed?")) {

        nextTick(() => {
            tracker.onlyReferral = true;
        })
    }
});

onMounted(() => {
    init();
})

async function init() {
    if (trackerId.value) {
        const {data} = await trackersService.getTrackerEdit(trackerId.value);
        populateForm(data);

        initDiligenceFields.value = tracker.diligenceFields.map(item => item.id);
        initReferral.value = tracker.onlyReferral;

        if (data.start_date !== null) {
            timeline.value = decodeIntervalFromServer([data.start_date, data.end_date]);
        }
    } else {
        tracker.type = trackerType.value;

        if ([trackerTypes.ECOSYSTEMS, trackerTypes.POC].includes(tracker.type)) {
            tracker.enablePipeline = false;
        }

        if (folderId.value) {
            const {data} = await trackersFoldersService.getInfo(folderId.value);
            tracker.permissions = data.permissions;
            tracker.userPermissions = data.user_permissions;
        } else {
            const {data} = await trackersService.getNewTrackerInfo();
            tracker.userPermissions = data.user_permissions;
        }
    }
}

async function save() {
    if (!submitterFieldsBlock.value.validateFields()) {
        return;
    }

    let removedFields = !!initDiligenceFields.value.filter(initFieldId => !tracker.diligenceFields.some(field => field.id === initFieldId)).length;

    if (!removedFields || confirm("Fields you removed from tracker will cause their values removing for existing startups. Do you want to proceed?")) {
        let form = {
            ...tracker,
            updatedEmailStages: pipelineBlock.value.emailStages || [],
            updatedEmailTemplates: pipelineBlock.value.emailTemplates || {},
            submitterFields: tracker.submitterFields.filter(field => submitterFieldsBlock.value.visibleFields.includes(field.field.id)),
        }

        loading.value = true;

        try {
            const {data} = await trackersService.save(trackerId.value, form);
            trackerId.value = data;

            let newFilesFields = tracker.submitterFields.filter(field => field.value.files.some(file => file.id === 0));
            newFilesFieldsCount.value = newFilesFields.length;

            if (newFilesFieldsCount.value) {
                newFilesFields.forEach(field => {
                    let formData = new FormData();

                    field.value.files.forEach(file => {
                        if (file.id === 0) {
                            formData.append('file[]', file);
                        }
                    });

                    trackersService.saveSubmitterFiles(trackerId.value, field.field.id, formData).then(() => {
                        newFilesFieldsCount.value--;

                        if (!newFilesFieldsCount.value) {
                            saveComplete();
                        }
                    });
                });
            } else {
                saveComplete();
            }
        } catch (e) {
            console.log(e);
            loading.value = false;
        }
    }
}

function saveComplete() {
    loading.value = false;
    emit('trackerSaved');

    Vue.notify({
        group: 'app',
        duration: 5000,
        type: 'success',
        text: !isNew.value ? "Tracker updated" : "Tracker created" + '<a class="btn btn-outline-success btn-notify btn-md" href="/trackers/' + trackerId.value + '" target="_blank">Open Tracker</a>'
    })
}

provide('tracker', tracker);
provide('canEditInfo', canEditInfo);
provide('timeline', timeline);
</script>

<template>
    <div>
        <SelectTemplate
            ref="selectTemplateBlock"
            :submitter-fields-list="submitterFieldsBlock?.fieldsList"
            :select-template-step.sync="selectTemplateStep"
        />

        <div v-show="!selectTemplateStep">
            <h1 class="modal__title mb-1">Add Tracker Details</h1>

            <Details
                :selected-template="selectTemplateBlock?.selectedTemplate || null"
                :has-templates="selectTemplateBlock?.hasTemplates || false"
                :select-template-step.sync="selectTemplateStep"
            />

            <SubmitterFields ref="submitterFieldsBlock"/>

            <Pipeline ref="pipelineBlock"/>

            <POC v-if="tracker.type === trackerTypes.POC"/>

            <Permissions/>

            <CustomFields/>

            <TimeLine/>

            <Categories/>

            <Sharing/>

            <Settings/>
        </div>

        <Footer
            v-if="!selectTemplateStep"
            :loading="loading"
            @save="save"
        />
    </div>
</template>

<style scoped>

</style>
